import { useCallback } from 'react';

import { useConversationContext } from '@/contexts/v2/ConversationContext';
import { useProfileSettings } from '@/hooks/v2/useProfileSettings';

import { ConversationInputBar } from './ConversationInputBar';
import { publishInjectUserTurn } from './injectUserTurn';

export type WiredConversationInputBarProps = {
  /** Shell mode the bar is rendered in. Drives the in-bar Maximize2/Minimize2 toggle visibility. */
  mode?: 'peek' | 'detailed' | 'focused';
  /** Flip Detailed ↔ Focused. Hidden in the bar if absent. */
  onToggleMode?: () => void;
  /** When false the bar hides its expand affordance. */
  expandable?: boolean;
  expanded?: boolean;
  onToggleExpand?: () => void;
  placeholder?: string;
};

/**
 * `ConversationInputBar` wired to `useConversationContext()` and
 * `useProfileSettings()` — mirrors the wiring that `ChatThread` does
 * internally, but without rendering the transcript. Used by
 * `ConversationShell` for the peek + detailed shell states (which both
 * show only the input bar, no thread).
 */
export function WiredConversationInputBar({
  mode,
  onToggleMode,
  expandable = true,
  expanded,
  onToggleExpand,
  placeholder,
}: WiredConversationInputBarProps) {
  const conversation = useConversationContext();
  const { setMode } = useProfileSettings();

  const {
    inputReady,
    voiceStartReady,
    inputMode,
    setInputMode,
    sendMessage,
    isThinking,
    isResponding,
    voiceState,
    paused,
    pauseReason,
    pausedMs,
    voiceStartedAt,
    disconnectVoice,
    pauseVoice,
    resumeVoice,
    error,
    connectVoice,
    room,
    livekitUrl,
    micBlocked,
    micErrorKind,
    micLocked,
    statusLabelOverride,
    voiceEndedReason,
    textStatusUpdate,
    idlePrompting,
    sessionCapWarning,
    extendSession,
    lockScopeRequested,
    requestLockScope,
  } = conversation;

  const handleSwitchToVoice = useCallback(() => {
    setInputMode('voice');
    void connectVoice();
    void setMode('voice');
  }, [setInputMode, connectVoice, setMode]);

  const handleSwitchToText = useCallback(() => {
    setInputMode('text');
    void setMode('text');
  }, [setInputMode, setMode]);

  const handleStartVoiceFromIdle = useCallback(() => connectVoice(), [connectVoice]);

  // ANA-542 — "Lock the scope" tapped on the session-cap wind-down banner.
  // Mirrors ChatThread.handleLockScope's voice-vs-text routing (039 phase 2
  // 1b): in a live voice session this must reach the agent via
  // inject_user_turn, not sendMessage's text-SSE path.
  // `lockScopeRequested`/`requestLockScope` come from the shared conversation
  // hook, not component-local state — see the `useVoiceConversation`
  // `lockScopeRequested` doc for why (this bar and `ChatThread` are two
  // separate mount points for the same banner; a local guard would reset on
  // the peek/detailed ↔ focused shell-mode switch and allow a double tap).
  const handleLockScope = useCallback(() => {
    if (lockScopeRequested) return;
    requestLockScope?.();
    const text = 'Lock the scope.';
    const publishedToVoice =
      inputMode === 'voice' && !!room?.localParticipant && publishInjectUserTurn(room, text);
    if (!publishedToVoice) {
      void sendMessage(text);
    }
  }, [lockScopeRequested, requestLockScope, inputMode, room, sendMessage]);

  const expandedProp = expandable ? expanded : undefined;
  const onToggleExpandProp = expandable ? onToggleExpand : undefined;

  return (
    <ConversationInputBar
      inputReady={inputReady}
      // Text input is always enabled — `useTextConversation.sendMessage`
      // lazy-mints `conversation_id` and the server's `/message` route
      // creates the mapping on demand. Same defaults `ChatThread` passes.
      textRequiresSessionReady={false}
      onSend={sendMessage}
      busy={isThinking || (isResponding ?? false)}
      placeholder={placeholder}
      inputMode={inputMode}
      onSwitchToVoice={handleSwitchToVoice}
      onSwitchToText={handleSwitchToText}
      onStartVoiceFromIdle={handleStartVoiceFromIdle}
      // A3.2 (ANA-658) — block a fresh voice start until the server id is committed,
      // so a scoping/proactive surface can't key the LiveKit room on a fallback id
      // that then diverges from the text session (voice↔text split-brain).
      voiceStartDisabled={!voiceStartReady}
      voiceState={voiceState}
      statusLabelOverride={statusLabelOverride}
      idlePrompting={idlePrompting}
      sessionCapWarning={sessionCapWarning && !lockScopeRequested}
      onExtendSession={extendSession}
      onLockScope={handleLockScope}
      textStatusUpdate={textStatusUpdate}
      room={room}
      livekitUrl={livekitUrl}
      micBlocked={micBlocked}
      micErrorKind={micErrorKind}
      micLocked={micLocked}
      paused={paused}
      pauseReason={pauseReason}
      voiceError={error}
      voiceEndedReason={voiceEndedReason}
      voiceStartedAt={voiceStartedAt}
      pausedMs={pausedMs}
      onPause={pauseVoice}
      onResume={resumeVoice}
      onEndCall={disconnectVoice}
      expanded={expandedProp}
      onToggleExpand={onToggleExpandProp}
      mode={mode}
      onToggleMode={onToggleMode}
    />
  );
}
